import SearchableSelect from '../components/search/SearchableSelect';
import React, { useState, useEffect, useCallback } from 'react';
import {
  useMediaQuery,
  useTheme,
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
  Tooltip,
  Zoom,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BlockIcon from '@mui/icons-material/Block';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { SmartToy as BotIcon } from '@mui/icons-material';
import TaxAdvisorModal from '../components/TaxAdvisorModal';
import {
  getTaxProfile,
  updateTaxProfile,
  getTaxIncomeItems,
  createTaxIncomeItem,
  updateTaxIncomeItem,
  retireTaxIncomeItem,
  getTaxAllocationLinks,
  createTaxAllocationLink,
  deleteTaxAllocationLink,
  getTaxPayments,
  confirmTaxPayment,
  updateTaxPayment,
  deleteTaxPayment,
  calculateTax,
  getTaxProgress,
  getC2CActuals,
  getAccounts,
  getCategories,
  getTaxAdvisorReport,
} from '../services/api';

function TaxHelperPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // eslint-disable-line no-unused-vars

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile state
  const [profile, setProfile] = useState(null); // eslint-disable-line no-unused-vars
  const [profileState, setProfileState] = useState('CA');
  const [bufferPct, setBufferPct] = useState('0.03');
  const [useSsOverpayment, setUseSsOverpayment] = useState(false);
  const [useQuarterlySurplus, setUseQuarterlySurplus] = useState(false);

  // Income items state
  const [incomeItems, setIncomeItems] = useState([]);
  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeKind, setIncomeKind] = useState('w2');
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeLabel, setIncomeLabel] = useState('');
  const [incomeEffectiveDate, setIncomeEffectiveDate] = useState('');

  // Allocation links state
  const [allocationLinks, setAllocationLinks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Quarterly payments state
  const [payments, setPayments] = useState([]);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentQuarter, setPaymentQuarter] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('paid'); // 'paid' or 'void'

  // Calculation state
  const [calculation, setCalculation] = useState(null);
  const [progress, setProgress] = useState(null);
  const [c2cActuals, setC2CActuals] = useState(null); // cached snapshot block — no page-load compute
  const [recommendationPeriod, setRecommendationPeriod] = useState('weekly');
  const [advisorReport, setAdvisorReport] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState('');
  const [advisorModalOpen, setAdvisorModalOpen] = useState(false);

  // Year selection state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const availableYears = [currentYear - 1, currentYear, currentYear + 1];

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const loadAdvisorReport = useCallback(async (refresh = false) => {
    setAdvisorLoading(true);
    setAdvisorError('');
    try {
      const response = await getTaxAdvisorReport(selectedYear, refresh);
      setAdvisorReport(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setAdvisorReport(null);
        setAdvisorError('Advisor snapshot is not ready for this year yet.');
      } else {
        setAdvisorReport(null);
        setAdvisorError(err.response?.data?.msg || 'Advisor temporarily unavailable.');
      }
    } finally {
      setAdvisorLoading(false);
    }
  }, [selectedYear]);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileRes, itemsRes, linksRes, paymentsRes, accountsRes, categoriesRes] = await Promise.all([
        getTaxProfile(),
        getTaxIncomeItems(),
        getTaxAllocationLinks(),
        getTaxPayments(selectedYear),
        getAccounts(),
        getCategories(),
      ]);

      setProfile(profileRes.data);
      setProfileState(profileRes.data.state);
      setBufferPct(profileRes.data.buffer_pct);
      setUseSsOverpayment(profileRes.data.use_ss_overpayment_in_projections);
      setUseQuarterlySurplus(profileRes.data.use_quarterly_surplus_in_projections || false);
      setIncomeItems(itemsRes.data);
      setAllocationLinks(linksRes.data);
      setPayments(paymentsRes.data);
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);

      // Load calculations if there are income items
      if (itemsRes.data.filter(i => i.is_active).length > 0) {
        const [calcRes, progressRes] = await Promise.all([
          calculateTax(selectedYear, recommendationPeriod),
          getTaxProgress(selectedYear),
        ]);
        setCalculation(calcRes.data);
        setProgress(progressRes.data);
        // Cached C2C actuals (pre-computed by the daily snapshot; cheap read, never computes)
        try {
          const c2cRes = await getC2CActuals(selectedYear);
          setC2CActuals(c2cRes.data);
        } catch (c2cErr) {
          console.error('Failed to load C2C actuals:', c2cErr);
          setC2CActuals(null);
        }
      } else {
        setCalculation(null);
        setProgress(null);
        setC2CActuals(null);
      }

    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.msg || 'Failed to load tax helper data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, recommendationPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle year change
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  // Recalculate when period changes
  const handlePeriodChange = async (event, newPeriod) => {
    if (newPeriod) {
      setRecommendationPeriod(newPeriod);
      if (incomeItems.filter(i => i.is_active).length > 0) {
        try {
          const calcRes = await calculateTax(selectedYear, newPeriod);
          setCalculation(calcRes.data);
        } catch (err) {
          console.error('Failed to recalculate:', err);
        }
      }
    }
  };

  const handleRefreshAdvisor = async () => {
    await loadAdvisorReport(true);
  };

  const handleOpenAdvisorModal = () => {
    setAdvisorModalOpen(true);
    // Lazy load: fetch report only if we don't have one cached
    if (!advisorReport && !advisorLoading) {
      loadAdvisorReport(false);
    }
  };

  // Profile handlers
  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    try {
      await updateTaxProfile({
        state: profileState,
        buffer_pct: parseFloat(bufferPct),
        use_ss_overpayment_in_projections: useSsOverpayment,
        use_quarterly_surplus_in_projections: useQuarterlySurplus,
      });
      setSuccess('Profile settings saved');
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save profile');
    }
  };

  // Income item handlers
  const handleOpenIncomeModal = (item = null) => {
    if (item) {
      setEditingIncome(item);
      setIncomeKind(item.kind);
      setIncomeAmount(item.annual_amount);
      setIncomeLabel(item.label || '');
      setIncomeEffectiveDate(item.effective_date || '');
    } else {
      setEditingIncome(null);
      setIncomeKind('w2');
      setIncomeAmount('');
      setIncomeLabel('');
      setIncomeEffectiveDate(new Date().toISOString().split('T')[0]);
    }
    setOpenIncomeModal(true);
  };

  const handleSaveIncome = async () => {
    setError('');
    try {
      if (editingIncome) {
        await updateTaxIncomeItem(editingIncome.id, {
          annual_amount: parseFloat(incomeAmount),
          label: incomeLabel,
          effective_date: incomeEffectiveDate,
        });
      } else {
        await createTaxIncomeItem({
          kind: incomeKind,
          annual_amount: parseFloat(incomeAmount),
          label: incomeLabel,
          effective_date: incomeEffectiveDate,
        });
      }
      setOpenIncomeModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save income item');
    }
  };

  const handleRetireIncome = async (item) => {
    setError('');
    try {
      await retireTaxIncomeItem(item.id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to retire income item');
    }
  };

  // Allocation link handlers
  const handleOpenLinkModal = () => {
    setSelectedAccount('');
    setSelectedCategory('');
    setOpenLinkModal(true);
  };

  const handleSaveLink = async () => {
    setError('');
    try {
      await createTaxAllocationLink(selectedAccount, selectedCategory);
      setOpenLinkModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to link category');
    }
  };

  const handleDeleteLink = async (linkId) => {
    setError('');
    try {
      await deleteTaxAllocationLink(linkId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to unlink category');
    }
  };

  // Payment handlers
  const handleOpenPaymentModal = (payment = null, quarter = null, initialStatus = 'paid') => {
    if (payment) {
      setEditingPayment(payment);
      setPaymentQuarter(payment.quarter);
      setPaymentAmount(payment.amount_paid);
      setPaymentDate(payment.payment_date);
      setPaymentNotes(payment.notes || '');
      setPaymentStatus(payment.status || 'paid');
    } else {
      setEditingPayment(null);
      setPaymentQuarter(quarter || 'Q1');
      setPaymentAmount(initialStatus === 'void' ? '0' : (calculation ? (calculation.allocation_per_quarter || '').toString() : ''));
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentNotes('');
      setPaymentStatus(initialStatus);
    }
    setOpenPaymentModal(true);
  };

  const handleSavePayment = async () => {
    setError('');
    try {
      if (editingPayment) {
        await updateTaxPayment(editingPayment.id, {
          amount_paid: paymentStatus === 'void' ? 0 : parseFloat(paymentAmount),
          payment_date: paymentDate,
          status: paymentStatus,
          notes: paymentNotes,
        });
      } else {
        await confirmTaxPayment({
          tax_year: selectedYear,
          quarter: paymentQuarter,
          amount_paid: paymentStatus === 'void' ? 0 : parseFloat(paymentAmount),
          payment_date: paymentDate,
          status: paymentStatus,
          notes: paymentNotes,
        });
      }
      setOpenPaymentModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    setError('');
    try {
      await deleteTaxPayment(paymentId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to delete payment');
    }
  };

  // Get filtered categories for selected account
  const filteredCategories = categories.filter(c => c.account_id === parseInt(selectedAccount) && !c.is_deleted);

  // Get confirmed payment for a quarter
  const getPaymentForQuarter = (quarter) => payments.find(p => p.quarter === quarter);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4">
          Tax Helper
        </Typography>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={handleYearChange}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
                {year === currentYear && ' (Current)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Estimate quarterly taxes and track your set-aside progress
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Settings</Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={profileState}
                  label="State"
                  onChange={(e) => setProfileState(e.target.value)}
                >
                  <MenuItem value="CA">California</MenuItem>
                  <MenuItem value="NV">Nevada</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Buffer %"
                type="number"
                value={parseFloat(bufferPct) * 100}
                onChange={(e) => setBufferPct((parseFloat(e.target.value) / 100).toString())}
                inputProps={{ min: 0, max: 50, step: 1 }}
                helperText="Safety buffer added to estimates"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={useSsOverpayment}
                    onChange={(e) => setUseSsOverpayment(e.target.checked)}
                  />
                }
                label="Use SS overpayment in projections"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Off = Conservative (recommended)
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={useQuarterlySurplus}
                    onChange={(e) => setUseQuarterlySurplus(e.target.checked)}
                  />
                }
                label="Use quarterly surplus in projections"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Off = Conservative (recommended)
              </Typography>

              <Button variant="contained" onClick={handleSaveProfile} fullWidth>
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Income Sources */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Income Sources</Typography>
                <Button startIcon={<AddIcon />} onClick={() => handleOpenIncomeModal()}>
                  Add Income
                </Button>
              </Box>

              {incomeItems.length === 0 ? (
                <Alert severity="info">No income sources added yet. Add your W-2 or C2C income to get started.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell align="right">Annual Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incomeItems.map((item) => (
                        <TableRow key={item.id} sx={{ opacity: item.is_active ? 1 : 0.5 }}>
                          <TableCell>
                            <Chip
                              label={item.kind.toUpperCase()}
                              size="small"
                              color={item.kind === 'w2' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>{item.label || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(item.annual_amount)}</TableCell>
                          <TableCell>
                            {item.is_active ? (
                              <Chip label="Active" size="small" color="success" />
                            ) : (
                              <Chip label="Retired" size="small" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {item.is_active && (
                              <>
                                <IconButton size="small" onClick={() => handleOpenIncomeModal(item)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleRetireIncome(item)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calculation Results & Progress */}
        {calculation && (
          <>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedYear} Tax Estimate
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Typography variant="caption">Annual Obligation</Typography>
                        <Typography variant="h5">{formatCurrency(calculation.allocation_needed_with_buffer)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <Typography variant="caption">
                          {progress?.current_quarter ? `${progress.current_quarter} Target` : 'Per Quarter (avg)'}
                        </Typography>
                        <Typography variant="h5">
                          {formatCurrency(progress?.current_quarter_target || calculation.allocation_per_quarter)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                        <Typography variant="caption">Gross Income</Typography>
                        <Typography variant="h5">{formatCurrency(calculation.gross_annual)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <Typography variant="caption">SS Overpayment</Typography>
                        <Typography variant="h5">{formatCurrency(calculation.ss_overpayment_expected)}</Typography>
                        <Typography variant="caption">
                          {calculation.ss_overpayment_applied ? 'Applied' : 'Not Applied'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Recommendation Period Toggle - based on REMAINING obligation */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle2">Set-aside recommendation:</Typography>
                    <ToggleButtonGroup
                      value={recommendationPeriod}
                      exclusive
                      onChange={handlePeriodChange}
                      size="small"
                    >
                      <ToggleButton value="weekly">Weekly</ToggleButton>
                      <ToggleButton value="bi-weekly">Bi-weekly</ToggleButton>
                      <ToggleButton value="monthly">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Show remaining-based recommendation if progress data available, otherwise annual-based */}
                  {progress ? (
                    <>
                      <Typography variant="h4" color={progress.remaining_setaside_needed > 0 ? 'primary' : 'success.main'}>
                        {formatCurrency(
                          recommendationPeriod === 'weekly' ? progress.remaining_weekly :
                          recommendationPeriod === 'bi-weekly' ? progress.remaining_biweekly :
                          progress.remaining_monthly
                        )} / {recommendationPeriod.replace('-', ' ')}
                      </Typography>
                      {progress.remaining_setaside_needed <= 0 ? (
                        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                          You've met your obligation for {selectedYear}!
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Based on {formatCurrency(progress.remaining_setaside_needed)} remaining to set aside
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="h4" color="primary">
                      {formatCurrency(calculation.allocation_per_period)} / {recommendationPeriod.replace('-', ' ')}
                    </Typography>
                  )}

                  {/* C2C Set-Aside Guidance */}
                  {calculation.c2c_setaside_pct > 0 && (
                    <Paper
                      variant="outlined"
                      sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderLeft: '4px solid', borderLeftColor: 'secondary.main' }}
                    >
                      <Typography variant="subtitle2" color="secondary.dark" gutterBottom>
                        C2C Set-Aside Guidance
                      </Typography>
                      <Typography variant="body2">
                        Set aside <strong>{Math.round(calculation.c2c_setaside_pct)}%</strong> of each gross C2C payment received
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Covers SE tax, federal bracket impact, and additional Medicare on your C2C income
                      </Typography>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* C2C Reality Check (live actuals, served from the cached daily snapshot) */}
            {calculation && c2cActuals && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        C2C Reality Check (live)
                      </Typography>
                      {c2cActuals.available && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`as of ${c2cActuals.generated_at ? new Date(c2cActuals.generated_at).toLocaleString() : 'unknown'}`}
                            variant="outlined"
                          />
                          {c2cActuals.is_stale && (
                            <Chip size="small" color="warning" label="stale — refreshes daily 05:00 UTC" />
                          )}
                        </Box>
                      )}
                    </Box>

                    {!c2cActuals.available ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Live actuals are computed once daily (05:00 UTC) and served from cache.
                        No cached computation exists yet — this card will populate after the next
                        scheduled snapshot.
                      </Alert>
                    ) : (() => {
                      const b = c2cActuals.c2c_actuals;
                      const ceiling = calculation.allocation_needed_with_buffer || 0;
                      const paidVsCeiling = (b.quarterlies_paid_ytd || 0) - ceiling;
                      const actualCushion = (b.quarterlies_paid_ytd || 0) - (b.aggregate_ytd_obligation || 0);
                      const nextPaymentLow = Math.max(0, (b.aggregate_ytd_obligation || 0) - (b.quarterlies_paid_ytd || 0));
                      const nextPaymentHigh = calculation.allocation_per_quarter || 0;
                      return (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Two views of the same year: the conservative ceiling assumes projected income
                            comes true; the live actual is computed from income really received. The space
                            between is yours to decide.
                          </Typography>

                          <Grid container spacing={2}>
                            {/* Conservative ceiling */}
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <Typography variant="overline" color="text.secondary">
                                  Conservative ceiling (projected)
                                </Typography>
                                <Typography variant="h5">{formatCurrency(ceiling)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Full-year obligation if projected annual income comes true (incl. buffer)
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2">
                                  Paid so far (all sources): {formatCurrency(b.quarterlies_paid_ytd)}
                                </Typography>
                                <Typography variant="body2">
                                  Reserve still held: {formatCurrency(b.setaside_reserve_balance)}
                                </Typography>
                                <Chip
                                  size="small"
                                  sx={{ mt: 1 }}
                                  color={paidVsCeiling >= 0 ? 'success' : 'default'}
                                  label={paidVsCeiling >= 0
                                    ? 'ceiling fully paid'
                                    : `${formatCurrency(-paidVsCeiling)} to reach the ceiling`}
                                />
                              </Paper>
                            </Grid>

                            {/* Live actual */}
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 2, height: '100%', borderLeft: '4px solid', borderLeftColor: b.covered ? 'success.main' : 'error.main' }}>
                                <Typography variant="overline" color="text.secondary">
                                  Live actual (income really received)
                                </Typography>
                                <Typography variant="h5">{formatCurrency(b.attributed_tax)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Real tax attributable to actual C2C received ({formatCurrency(b.realized_c2c_ytd)} —
                                  {' '}{b.effective_rate_pct}% effective, aggregate-aware)
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2">
                                  Set aside from those payments: {formatCurrency(b.setaside_inflows_ytd)}
                                </Typography>
                                <Chip
                                  size="small"
                                  sx={{ mt: 1 }}
                                  color={b.covered ? 'success' : 'error'}
                                  label={b.covered
                                    ? `COVERED — ${formatCurrency(b.position)} above what's owed on real income`
                                    : `SHORT ${formatCurrency(-b.position)} vs real income owed`}
                                />
                              </Paper>
                            </Grid>
                          </Grid>

                          {/* Expandable detail panels */}
                          <Accordion sx={{ mt: 2 }} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">Conservative ceiling — details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="body2">
                                Annual obligation (with buffer): {formatCurrency(ceiling)} ·
                                per quarter (avg): {formatCurrency(calculation.allocation_per_quarter)} ·
                                projected gross: {formatCurrency(calculation.gross_annual)}
                                {' '}(W-2 {formatCurrency(calculation.gross_w2)} + C2C {formatCurrency(calculation.gross_c2c)})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                This is the existing static estimate, unchanged — deliberately conservative;
                                it assumes projected annual incomes come true.
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                          <Accordion disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">Live actuals — details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="body2">
                                Aggregate YTD obligation beyond withholding (all sources): {formatCurrency(b.aggregate_ytd_obligation)} ·
                                quarterlies paid: {formatCurrency(b.quarterlies_paid_ytd)}
                                {' '}({actualCushion >= 0 ? `${formatCurrency(actualCushion)} ahead` : `${formatCurrency(-actualCushion)} behind`})
                              </Typography>
                              <Typography variant="body2">
                                Attributed C2C tax with your buffer: {formatCurrency(b.attributed_tax_with_buffer)} ·
                                last C2C payment received: {b.last_c2c_payment_date || 'n/a'}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Next quarterly payment, right-sized (federal): between
                                {' '}<strong>{formatCurrency(nextPaymentLow)}</strong> (actual-owed basis) and
                                {' '}<strong>{formatCurrency(nextPaymentHigh)}</strong> (conservative per-quarter target).
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                Payments aren't tagged by income source, so "paid" is shown at the all-sources
                                level only. Attribution method: incremental tax the real C2C adds on top of the
                                W-2 base (never a flat rate on C2C in isolation).
                              </Typography>
                            </AccordionDetails>
                          </Accordion>

                          <Tooltip title={`Realized C2C uses the account-level income sum, which includes a small amount of non-paycheck inflows (~$1.5k: starter balance/corrections). This only ever increases computed owed — i.e. errs toward reserving more.`}>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5, cursor: 'help' }}>
                              Conservative — includes a small safety margin. W-2 withholding is modeled
                              (enter actual YTD withheld from a paystub for precision — coming soon);
                              pre-tax 401k/benefits are not in the data; supplemental-rate (RSU) withholding
                              may differ from the model.
                            </Typography>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Progress Tracking */}
            {progress && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {progress.is_future_year ? `${selectedYear} Planning` :
                       progress.is_past_year ? `${selectedYear} History` :
                       'Progress'}
                    </Typography>

                    {/* Year Context Banner - Show at TOP for future/past years */}
                    {(progress.is_future_year || progress.is_past_year) && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: progress.is_future_year ? 'info.light' : 'grey.200', borderRadius: 1 }}>
                        <Typography variant="body2" color={progress.is_future_year ? 'info.contrastText' : 'text.primary'}>
                          {progress.is_future_year
                            ? `Planning view for ${selectedYear}. Projected obligation: ${formatCurrency(progress.annual_obligation)} across 4 quarters.`
                            : `Historical view for ${selectedYear}. ${progress.remaining_quarters_count === 0 ? 'All obligations were addressed.' : `${progress.remaining_quarters_count} quarter(s) were not addressed.`}`
                          }
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {progress.is_future_year ? 'Planned' : 'Confirmed'}: {formatCurrency(progress.confirmed_total)} of {formatCurrency(progress.annual_obligation)}
                        </Typography>
                        <Typography variant="body2">{(progress.overall_progress_pct || 0).toFixed(1)}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress.overall_progress_pct || 0, 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color={progress.is_future_year ? 'info' : 'primary'}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {progress.is_future_year ? 'Planned Payments' : 'Confirmed Payments'}
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatCurrency(progress.confirmed_total)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">Set-Aside Balance</Typography>
                          <Typography variant="h6" color="info.main">
                            {formatCurrency(progress.projected_setaside)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {progress.is_current_year ? `${(progress.setaside_vs_current_quarter_pct || 0).toFixed(0)}% of current Q` : 'Current balance'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">Total Surplus</Typography>
                          <Typography variant="h6" color={(progress.total_surplus || 0) > 0 ? 'success.main' : 'text.secondary'}>
                            {formatCurrency(progress.total_surplus || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {progress.surplus_applied ? 'Applied' : 'Not applied'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {progress.is_future_year ? 'Total Obligation' : 'Remaining Annual'}
                          </Typography>
                          <Typography variant="h6" color="warning.main">
                            {formatCurrency(progress.remaining_annual_adjusted || progress.remaining_annual || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {progress.remaining_quarters_count || 0} quarters {progress.is_future_year ? 'to plan' : 'left'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Current Year Status Section - Only shown for current year */}
                    {progress.is_current_year && (() => {
                      const allQuartersDone = progress.remaining_quarters_count === 0;
                      const currentQStatus = progress.quarters_status?.[progress.current_quarter];
                      const isCurrentQPaid = currentQStatus?.status === 'paid' || currentQStatus?.status === 'overpaid';
                      const isCurrentQVoid = currentQStatus?.status === 'void';

                      if (allQuartersDone) {
                        return (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="success.contrastText">
                              {selectedYear} Complete
                            </Typography>
                            <Typography variant="body2" color="success.contrastText">
                              All quarterly obligations have been addressed.
                              {(progress.total_surplus || 0) > 0 && ` Surplus: ${formatCurrency(progress.total_surplus)}`}
                            </Typography>
                          </Box>
                        );
                      } else if (isCurrentQPaid) {
                        return (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="success.contrastText">
                              {progress.current_quarter} Paid
                            </Typography>
                            <Typography variant="body2" color="success.contrastText">
                              Paid {formatCurrency(currentQStatus?.confirmed_amount || 0)} on {currentQStatus?.payment_date}
                              {(currentQStatus?.surplus || 0) > 0 && ` (surplus: ${formatCurrency(currentQStatus.surplus)})`}
                            </Typography>
                          </Box>
                        );
                      } else if (isCurrentQVoid) {
                        return (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {progress.current_quarter}: N/A
                            </Typography>
                            <Typography variant="body2">
                              No payment required for this quarter
                            </Typography>
                          </Box>
                        );
                      } else {
                        return (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Current Quarter: {progress.current_quarter}
                            </Typography>
                            <Typography variant="body2">
                              Target: {formatCurrency(progress.current_quarter_target || progress.quarterly_target)} |
                              Set-aside covers: {(progress.setaside_vs_current_quarter_pct || 0).toFixed(0)}%
                            </Typography>
                          </Box>
                        );
                      }
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </>
        )}

        {/* Quarterly Payments */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quarterly Payments {progress?.is_future_year && '(Planning)'}
              </Typography>

              <Box>
                {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => {
                  const payment = getPaymentForQuarter(quarter);
                  const quarterStatus = progress?.quarters_status?.[quarter];
                  const quarterInfo = {
                    Q1: { period: 'Jan-Mar', due: 'Apr 15' },
                    Q2: { period: 'Apr-May', due: 'Jun 15' },
                    Q3: { period: 'Jun-Aug', due: 'Sep 15' },
                    Q4: { period: 'Sep-Dec', due: 'Jan 15' },
                  }[quarter];

                  const isVoid = payment?.status === 'void';
                  const isPaid = payment?.status === 'paid';
                  const surplus = quarterStatus?.surplus;
                  const isCurrent = quarterStatus?.is_current;
                  const isFutureYear = progress?.is_future_year;
                  const isPastYear = progress?.is_past_year;

                  // Determine secondary text based on context
                  // Use display_target_amount for consistent quarterly targets (handles $0 cumulative gap quarters)
                  const displayTarget = quarterStatus?.display_target_amount || quarterStatus?.target_amount || calculation?.allocation_per_quarter || 0;
                  const getSecondaryText = () => {
                    if (isVoid) return 'Marked as N/A';
                    if (isPaid) return `Paid ${formatCurrency(payment.amount_paid)} on ${payment.payment_date}${surplus > 0 ? ' (surplus)' : ''}`;
                    if (isFutureYear) return `Due ${quarterInfo.due} ${selectedYear + (quarter === 'Q4' ? 1 : 0)} | Target: ${formatCurrency(displayTarget)}`;
                    if (isPastYear) return displayTarget > 0 ? `Was due ${quarterInfo.due} | Target was: ${formatCurrency(displayTarget)}` : 'No obligation';
                    if (isCurrent) return `Due ${quarterInfo.due} | Target: ${formatCurrency(displayTarget)} | Set-aside: ${quarterStatus?.status_display || '0%'}`;
                    return `Due ${quarterInfo.due} - ${formatCurrency(displayTarget)} target`;
                  };

                  return (
                    <Box
                      key={quarter}
                      sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: isCurrent && !isFutureYear && !isPastYear ? 'action.hover' : 'transparent',
                        opacity: isVoid ? 0.6 : 1,
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      {/* Top row: Quarter info and actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        {/* Left side: Quarter label and chips */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {isVoid ? (
                            <BlockIcon color="disabled" fontSize="small" />
                          ) : isPaid ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : isCurrent && !isFutureYear && !isPastYear ? (
                            <TrendingUpIcon color="primary" fontSize="small" />
                          ) : (
                            <ScheduleIcon color="action" fontSize="small" />
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{quarter}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({quarterInfo.period})
                          </Typography>
                          {isCurrent && !isFutureYear && !isPastYear && (
                            <Chip label="Current" size="small" color="primary" variant="outlined" />
                          )}
                          {isFutureYear && !payment && (
                            <Chip label="Planned" size="small" color="info" variant="outlined" />
                          )}
                          {surplus > 0 && (
                            <Chip
                              label={`+${formatCurrency(surplus)}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        {/* Right side: Action buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          {payment ? (
                            <>
                              <IconButton size="small" onClick={() => handleOpenPaymentModal(payment)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeletePayment(payment.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenPaymentModal(null, quarter, 'paid')}
                                sx={{ minWidth: 'auto', px: 1.5 }}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => handleOpenPaymentModal(null, quarter, 'void')}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                N/A
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Bottom row: Secondary text */}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                        {getSecondaryText()}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Set-Aside Categories */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Set-Aside Categories</Typography>
                <Button startIcon={<LinkIcon />} onClick={handleOpenLinkModal} size="small">
                  Link Category
                </Button>
              </Box>

              {allocationLinks.length === 0 ? (
                <Alert severity="info">
                  Link categories to track your set-aside progress. The balance in linked categories will show as "Projected Set-Aside".
                </Alert>
              ) : (
                <List>
                  {allocationLinks.map((link) => (
                    <ListItem key={link.id} divider>
                      <ListItemText
                        primary={link.category_name}
                        secondary={link.account_name}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => handleDeleteLink(link.id)}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Tax Breakdown Accordion */}
        {calculation && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Detailed Tax Breakdown</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Tax Calculations</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Federal Income Tax</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.federal_income_tax_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>State Income Tax</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.state_income_tax_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SE Social Security</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.se_ss_tax)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SE Medicare</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.se_medicare_tax)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Additional Medicare</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.addl_medicare_due_total)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>W-2 Withholdings</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Federal Withheld</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.w2_federal_withheld)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>State Withheld</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.w2_state_withheld)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SS Withheld</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.w2_ss_withheld)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Medicare Withheld</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.w2_medicare_withheld)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SDI Withheld</TableCell>
                          <TableCell align="right">{formatCurrency(calculation.w2_sdi_withheld)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>

      {/* Income Modal */}
      <Dialog open={openIncomeModal} onClose={() => setOpenIncomeModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIncome ? 'Edit Income Source' : 'Add Income Source'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editingIncome && (
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={incomeKind}
                  label="Type"
                  onChange={(e) => setIncomeKind(e.target.value)}
                >
                  <MenuItem value="w2">W-2 (Employee)</MenuItem>
                  <MenuItem value="c2c">C2C / 1099 (Contractor)</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Annual Amount"
              type="number"
              value={incomeAmount}
              onChange={(e) => setIncomeAmount(e.target.value)}
              InputProps={{ startAdornment: '$' }}
            />

            <TextField
              fullWidth
              label="Label (optional)"
              value={incomeLabel}
              onChange={(e) => setIncomeLabel(e.target.value)}
              placeholder="e.g., Main Job, Consulting"
            />

            <TextField
              fullWidth
              label="Effective Date"
              type="date"
              value={incomeEffectiveDate}
              onChange={(e) => setIncomeEffectiveDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIncomeModal(false)}>Cancel</Button>
          <Button onClick={handleSaveIncome} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Link Category Modal */}
      <Dialog open={openLinkModal} onClose={() => setOpenLinkModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Set-Aside Category</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Account</InputLabel>
              <SearchableSelect
                value={selectedAccount}
                label="Account"
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  setSelectedCategory('');
                }}
              >
                {accounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>

            <FormControl fullWidth disabled={!selectedAccount}>
              <InputLabel>Category</InputLabel>
              <SearchableSelect
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {filteredCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkModal(false)}>Cancel</Button>
          <Button onClick={handleSaveLink} variant="contained" disabled={!selectedCategory}>Link</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPayment
            ? 'Edit Payment'
            : paymentStatus === 'void'
            ? 'Mark Quarter as N/A'
            : 'Confirm Payment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editingPayment && (
              <FormControl fullWidth>
                <InputLabel>Quarter</InputLabel>
                <Select
                  value={paymentQuarter}
                  label="Quarter"
                  onChange={(e) => setPaymentQuarter(e.target.value)}
                >
                  <MenuItem value="Q1">Q1 (Jan-Mar)</MenuItem>
                  <MenuItem value="Q2">Q2 (Apr-May)</MenuItem>
                  <MenuItem value="Q3">Q3 (Jun-Aug)</MenuItem>
                  <MenuItem value="Q4">Q4 (Sep-Dec)</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={paymentStatus}
                label="Status"
                onChange={(e) => {
                  setPaymentStatus(e.target.value);
                  if (e.target.value === 'void') {
                    setPaymentAmount('0');
                  }
                }}
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="void">N/A (Void - no payment needed)</MenuItem>
              </Select>
            </FormControl>

            {paymentStatus === 'paid' && (
              <TextField
                fullWidth
                label="Amount Paid"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                InputProps={{ startAdornment: '$' }}
              />
            )}

            {paymentStatus === 'void' && (
              <Alert severity="info">
                Marking this quarter as N/A indicates no payment was needed (e.g., no tax obligation existed at that time).
              </Alert>
            )}

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={paymentStatus === 'void' ? 'Date marked as N/A' : 'Payment date'}
            />

            <TextField
              fullWidth
              label="Notes (optional)"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              multiline
              rows={2}
              placeholder={paymentStatus === 'void' ? 'e.g., "No tax obligation - job started later"' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentModal(false)}>Cancel</Button>
          <Button onClick={handleSavePayment} variant="contained">
            {editingPayment ? 'Update' : paymentStatus === 'void' ? 'Mark as N/A' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tax Advisor FAB */}
      <Tooltip title="Tax Advisor" placement="left">
        <Zoom in>
          <Fab
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              bgcolor: advisorReport ? 'primary.main' : 'grey.200',
              color: advisorReport ? 'white' : 'text.secondary',
              '&:hover': {
                bgcolor: advisorReport ? 'primary.dark' : 'grey.300',
              },
              ...(advisorReport ? {
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                },
              } : {}),
            }}
            onClick={handleOpenAdvisorModal}
            size="medium"
          >
            <BotIcon />
          </Fab>
        </Zoom>
      </Tooltip>

      {/* Tax Advisor Modal */}
      <TaxAdvisorModal
        open={advisorModalOpen}
        onClose={() => setAdvisorModalOpen(false)}
        report={advisorReport}
        loading={advisorLoading}
        error={advisorError}
        onRefresh={handleRefreshAdvisor}
        onGenerate={() => loadAdvisorReport(false)}
      />
    </Container>
  );
}

export default TaxHelperPage;
